import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { AlertCircle, CheckCircle2, Shield, Plus, X, Play, Search, Loader2, MessageSquare } from "lucide-react";
import combinedData from "../../data/Combined.json";
import ControlSelectorModal from "./ControlSelectorModal";
import ProcedureSelectorModal from "./ProcedureSelectorModal";
import InternalControlViewerModal from "../libraries/InternalControlViewerModal";
import { CONTROL_TEMPLATES } from "../../constants/ControlTemplates";
import { 
  associateControlWithRomm, 
  disassociateControlFromRomm, 
  getControlsForRomm,
  loadPayrollMetadata 
} from "../../utils/payroll-metadata";
import { getAssertionAbbreviation, parseAssertions } from "../../utils/assertion-helpers";
import { useArchivalMode } from "../../contexts/ArchivalModeContext";
import { ReviewComment } from "../shared/ReviewComment";

interface RommItem {
  id: string;
  risk: string;
  assertion: string;
  options: string[];
  selectedOption?: string;
  workspace?: string | string[];
}

interface InternalControl {
  control_id: string;
  control_name: string;
  control_type: string;
  control_attribute: string;
  control_description: string;
}

interface SubstantiveProcedure {
  id: string;
  name: string;
  description: string;
  requiredFiles: string[];
  optionalFiles?: string[];
  questions?: QuestionConfig[];
}

interface QuestionConfig {
  id: string;
  question: string;
  type: string;
  required: boolean;
  options?: string[];
}

interface PayrollRommsProps {
  onBack: () => void;
  onComplete: (data: Record<string, string>) => void;
  workspace?: string; // Optional workspace prop, defaults to "Employee Benefits Expense"
  rommsOverride?: RommItem[];
}

function getWorkspaceFallbackRomms(workspace: string): RommItem[] | null {
  if (workspace === "Other Financial Assets") {
    return [
      {
        id: "OFA.SR.001",
        risk: "Other financial assets do not relate to valid transactions, and/or are not recorded in proper account and/or are recorded at incorrect amount, and/or entity does not have rights to other assets, and/or are incomplete.",
        assertion: "Valuation, Completeness, Rights & Obligations, Existence",
        options: ["Lower", "Higher", "Significant", "NRPMM"],
        workspace: "Other Financial Assets",
      },
      {
        id: "OFA.SR.002",
        risk: "Other financial assets are inappropriately classified between current and non-current portion in financial statements.",
        assertion: "Classification",
        options: ["Lower", "Higher", "Significant", "NRPMM"],
        workspace: "Other Financial Assets",
      },
    ];
  }
  return null;
}

export default function PayrollRomms({
  onBack,
  onComplete,
  workspace = "Employee Benefits Expense",
  rommsOverride,
}: PayrollRommsProps) {
  const { isArchivalMode } = useArchivalMode();
  const [romms, setRomms] = useState<RommItem[]>([]);
  const [selectedRommId, setSelectedRommId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [rommSelections, setRommSelections] = useState<Record<string, string>>({});
  const [rommDocumentation, setRommDocumentation] = useState<Record<string, string>>({});
  const [associatedControls, setAssociatedControls] = useState<Record<string, InternalControl[]>>({});
  const [associatedProcedures, setAssociatedProcedures] = useState<Record<string, SubstantiveProcedure[]>>({});
  const [isControlSelectorOpen, setIsControlSelectorOpen] = useState(false);
  const [isProcedureSelectorOpen, setIsProcedureSelectorOpen] = useState(false);
  const [employeeBenefitsNoteLines, setEmployeeBenefitsNoteLines] = useState<string[]>([]);
  
  // Control viewer modal state
  const [selectedControlForViewing, setSelectedControlForViewing] = useState<any>(null);
  const [controlViewerTemplate, setControlViewerTemplate] = useState<any>(null);
  const [isControlViewerOpen, setIsControlViewerOpen] = useState(false);
  
  // Autosave status state
  const [autosaveStatus, setAutosaveStatus] = useState<{
    [rommId: string]: {
      status: 'saving' | 'saved' | 'error';
      lastSaved?: Date;
      error?: string;
    }
  }>({});

  // Review comment modal state
  const [reviewCommentOpen, setReviewCommentOpen] = useState(false);

  // Load persisted ROMM-control linkages
  useEffect(() => {
    try {
      const savedLinkages = localStorage.getItem('rommControlLinkages');
      if (savedLinkages) {
        setAssociatedControls(JSON.parse(savedLinkages));
      }
    } catch (error) {
      console.error('Error loading ROMM-control linkages:', error);
    }
  }, []);

  // Load ROMMS data from SharePoint for the specified workspace
  useEffect(() => {
    const loadRommsData = async () => {
      // If override is provided, always trust it (including empty while upstream data loads)
      // and never fall back to default workspace ROMMs.
      if (rommsOverride !== undefined) {
        const overrideRommIds = new Set(
          rommsOverride.map((romm) => String(romm.id).trim()).filter(Boolean),
        );
        setRomms(rommsOverride);
        setSelectedRommId(rommsOverride.length > 0 ? rommsOverride[0].id : null);
        setRommSelections({});
        setRommDocumentation({});
        setAssociatedControls({});
        setAssociatedProcedures({});

        // Even when ROMMs are provided by override (e.g. Revenue from Operations),
        // load any previously saved SharePoint values for matching ROMM IDs.
        if (
          overrideRommIds.size > 0 &&
          window.sharePointAPI?.readRommLibraryByWorkspace
        ) {
          try {
            const sharePointResponse =
              await window.sharePointAPI.readRommLibraryByWorkspace(workspace);

            if (sharePointResponse?.success && sharePointResponse.data?.romm_library) {
              const savedSelections: Record<string, string> = {};
              const savedDocumentation: Record<string, string> = {};
              const loadedControls: Record<string, InternalControl[]> = {};
              const loadedProcedures: Record<string, SubstantiveProcedure[]> = {};

              const availableProcedures: Record<string, SubstantiveProcedure> = {
                "IPE Testing": {
                  id: "ipe_testing",
                  name: "IPE Testing",
                  description: "Information Produced by Entity Testing",
                  requiredFiles: ["Pay Register", "CTC Report"],
                },
                "Exception Testing": {
                  id: "exception_testing",
                  name: "Exception Testing",
                  description: "Identify and analyze payroll exceptions",
                  requiredFiles: ["Pay Register", "CTC Report"],
                },
                "Increment Analysis": {
                  id: "increment_analysis",
                  name: "Increment Analysis",
                  description: "Analyze salary increments and changes",
                  requiredFiles: ["Pay Register", "CTC Report"],
                },
                "Headcount Reconciliation": {
                  id: "headcount_reconciliation",
                  name: "Headcount Reconciliation",
                  description: "Reconcile headcount data",
                  requiredFiles: ["Pay Register"],
                },
                "Actuary Testing": {
                  id: "actuary_testing",
                  name: "Actuary Testing",
                  description: "Actuarial valuation testing",
                  requiredFiles: ["Actuary Report"],
                },
                "Managerial Remuneration": {
                  id: "managerial_remuneration",
                  name: "Managerial Remuneration",
                  description:
                    "Section 197/198 compliance and Schedule V calculations",
                  requiredFiles: ["Directors Report"],
                },
                "Share Based Payment": {
                  id: "share_based_payment",
                  name: "Share Based Payment",
                  description: "Share based payment procedures",
                  requiredFiles: [],
                },
              };

              let librariesRcm: Record<string, any> = {};
              if (window.sharePointAPI?.downloadRcmJson) {
                try {
                  const rcmRes = await window.sharePointAPI.downloadRcmJson();
                  if (rcmRes.success && rcmRes.data) {
                    librariesRcm =
                      rcmRes.data.libraries_RCM || rcmRes.data.libraries_rcm || {};
                  }
                } catch (error) {
                  console.error(
                    "Error loading controls from Libraries_RCM.json in override mode:",
                    error,
                  );
                }
              }

              sharePointResponse.data.romm_library.forEach((entry: any) => {
                const rommId = String(entry["romm-id"] || "").trim();
                if (!rommId || !overrideRommIds.has(rommId)) {
                  return;
                }

                if (entry["assesment"] && entry["assesment"] !== "") {
                  savedSelections[rommId] = entry["assesment"];
                }
                if (entry["documentation"] && entry["documentation"] !== "") {
                  savedDocumentation[rommId] = entry["documentation"];
                }

                let controlIds: string[] = [];
                if (entry.control_id !== undefined && entry.control_id !== null) {
                  if (Array.isArray(entry.control_id)) {
                    controlIds = entry.control_id
                      .map((id: any) => String(id).trim())
                      .filter((id: string) => id.length > 0);
                  } else {
                    const singleId = String(entry.control_id).trim();
                    if (singleId.length > 0) {
                      controlIds = [singleId];
                    }
                  }
                }

                if (controlIds.length > 0) {
                  const controlsForRomm: InternalControl[] = [];
                  controlIds.forEach((controlId) => {
                    let controlEntry = librariesRcm[controlId];
                    if (!controlEntry) {
                      const matchingKey = Object.keys(librariesRcm).find(
                        (key) => key.toLowerCase() === controlId.toLowerCase(),
                      );
                      if (matchingKey) {
                        controlEntry = librariesRcm[matchingKey];
                      }
                    }

                    if (controlEntry) {
                      controlsForRomm.push({
                        control_id: controlId,
                        control_name:
                          controlEntry.control_name ||
                          controlEntry.controlName ||
                          controlId,
                        control_type:
                          controlEntry.Nature || controlEntry.subtype || "Unknown",
                        control_attribute:
                          controlEntry.TypeOfControl ||
                          controlEntry.typeOfControl ||
                          "Unknown",
                        control_description:
                          controlEntry.control_description ||
                          controlEntry.control_summary?.control_description ||
                          "",
                      });
                    } else {
                      controlsForRomm.push({
                        control_id: controlId,
                        control_name: controlId,
                        control_type: "Unknown",
                        control_attribute: "Unknown",
                        control_description: `Control ${controlId} (not found in Libraries_RCM.json)`,
                      });
                    }
                  });
                  loadedControls[rommId] = controlsForRomm;
                }

                let procedureIds: string[] = [];
                if (entry.procedure_id !== undefined && entry.procedure_id !== null) {
                  if (Array.isArray(entry.procedure_id)) {
                    procedureIds = entry.procedure_id
                      .map((id: any) => String(id).trim())
                      .filter((id: string) => id.length > 0);
                  } else {
                    const singleId = String(entry.procedure_id).trim();
                    if (singleId.length > 0) {
                      procedureIds = [singleId];
                    }
                  }
                }

                if (procedureIds.length > 0) {
                  loadedProcedures[rommId] = procedureIds.map((procedureName) => {
                    let procedure = availableProcedures[procedureName];
                    if (!procedure) {
                      const matchingKey = Object.keys(availableProcedures).find(
                        (key) => key.toLowerCase() === procedureName.toLowerCase(),
                      );
                      if (matchingKey) {
                        procedure = availableProcedures[matchingKey];
                      }
                    }

                    return (
                      procedure || {
                        id: procedureName.toLowerCase().replace(/\s+/g, "_"),
                        name: procedureName,
                        description: `${procedureName} procedure`,
                        requiredFiles: [],
                      }
                    );
                  });
                }
              });

              setRommSelections(savedSelections);
              setRommDocumentation(savedDocumentation);
              setAssociatedControls(loadedControls);
              setAssociatedProcedures(loadedProcedures);
            }
          } catch (error) {
            console.error(
              "Error loading SharePoint ROMM values for override workspace:",
              error,
            );
          }
        }
        return;
      }

      try {
        // Check if SharePoint API is available
        console.log("SharePoint API available:", !!window.sharePointAPI);
        console.log("SharePoint updateRommEntry available:", !!window.sharePointAPI?.updateRommEntry);
        
        // Try to fetch from SharePoint first
        if (window.sharePointAPI?.readRommLibraryByWorkspace) {
          console.log(`Fetching ROMM data from SharePoint for ${workspace}...`);
          const sharePointResponse = await window.sharePointAPI.readRommLibraryByWorkspace(workspace);
          
          if (sharePointResponse.success && sharePointResponse.data?.romm_library?.length) {
            console.log("Successfully loaded ROMM data from SharePoint:", sharePointResponse.data);
            
            // Transform SharePoint data to component format
            const rommsData: RommItem[] = sharePointResponse.data.romm_library.map((entry: any) => ({
              id: entry["romm-id"],
              risk: entry.description, // Use description as the risk text
              assertion: entry.assertion,
              options: ["Lower", "Higher", "Significant", "NRPMM"], // Standard options
              workspace: entry.workspace || workspace, // Include workspace from ROMM entry or use component workspace
            }));

            setRomms(rommsData);
            if (rommsData.length > 0) {
              setSelectedRommId(rommsData[0].id);
            }

            // Load saved assessments and documentation from SharePoint
            const savedSelections: Record<string, string> = {};
            const savedDocumentation: Record<string, string> = {};
            
            sharePointResponse.data.romm_library.forEach((entry: any) => {
              if (entry["assesment"] && entry["assesment"] !== "") {
                savedSelections[entry["romm-id"]] = entry["assesment"];
              }
              if (entry["documentation"] && entry["documentation"] !== "") {
                savedDocumentation[entry["romm-id"]] = entry["documentation"];
              }
            });

            console.log("Loaded saved assessments from SharePoint:", savedSelections);
            console.log("Loaded saved documentation from SharePoint:", savedDocumentation);
            
            setRommSelections(savedSelections);
            setRommDocumentation(savedDocumentation);

            // Load associated controls and procedures from Libraries_Romm.json
            const loadedControls: Record<string, InternalControl[]> = {};
            const loadedProcedures: Record<string, SubstantiveProcedure[]> = {};

            // Load controls from Libraries_RCM.json
            if (window.sharePointAPI?.downloadRcmJson) {
              try {
                const rcmRes = await window.sharePointAPI.downloadRcmJson();
                if (rcmRes.success && rcmRes.data) {
                  const rcmData = rcmRes.data;
                  const librariesRcm = rcmData.libraries_RCM || rcmData.libraries_rcm || {};

                  // Process each ROMM entry to load its associated controls
                  for (const entry of sharePointResponse.data.romm_library) {
                    const rommId = entry["romm-id"];
                    
                    // Handle control_id field - can be array, string, or undefined
                    let controlIds: string[] = [];
                    if (entry.control_id !== undefined && entry.control_id !== null) {
                      if (Array.isArray(entry.control_id)) {
                        controlIds = entry.control_id.map((id: any) => String(id).trim()).filter((id: string) => id.length > 0);
                      } else {
                        const singleId = String(entry.control_id).trim();
                        if (singleId.length > 0) {
                          controlIds = [singleId];
                        }
                      }
                    }

                    console.log(`[PayrollRomms] ROMM ${rommId} has control_ids:`, controlIds);

                    if (controlIds.length > 0) {
                      const controlsForRomm: InternalControl[] = [];
                      
                      for (const controlId of controlIds) {
                        // Try exact match first
                        let controlEntry = librariesRcm[controlId];
                        
                        // If not found, try case-insensitive match
                        if (!controlEntry) {
                          const matchingKey = Object.keys(librariesRcm).find(
                            key => key.toLowerCase() === controlId.toLowerCase()
                          );
                          if (matchingKey) {
                            controlEntry = librariesRcm[matchingKey];
                            console.log(`[PayrollRomms] Found control ${controlId} via case-insensitive match: ${matchingKey}`);
                          }
                        }
                        
                        if (controlEntry) {
                          controlsForRomm.push({
                            control_id: controlId,
                            control_name: controlEntry.control_name || controlEntry.controlName || controlId,
                            control_type: controlEntry.Nature || controlEntry.subtype || "Unknown",
                            control_attribute: controlEntry.TypeOfControl || controlEntry.typeOfControl || "Unknown",
                            control_description: controlEntry.control_description || controlEntry.control_summary?.control_description || "",
                          });
                          console.log(`[PayrollRomms] Successfully loaded control ${controlId} for ROMM ${rommId}`);
                        } else {
                          // Control exists in Libraries_Romm.json but not in Libraries_RCM.json
                          // Still add it with basic info so it shows in the UI
                          console.warn(`[PayrollRomms] Control ${controlId} found in ROMM ${rommId} but not in Libraries_RCM.json. Adding with basic info.`);
                          controlsForRomm.push({
                            control_id: controlId,
                            control_name: controlId, // Use control ID as name if not found
                            control_type: "Unknown",
                            control_attribute: "Unknown",
                            control_description: `Control ${controlId} (not found in Libraries_RCM.json)`,
                          });
                        }
                      }
                      
                      // Always set controls, even if some weren't found in Libraries_RCM.json
                      if (controlsForRomm.length > 0) {
                        loadedControls[rommId] = controlsForRomm;
                        console.log(`[PayrollRomms] Loaded ${controlsForRomm.length} controls for ROMM ${rommId} (requested: ${controlIds.length}):`, controlIds);
                      } else {
                        console.warn(`[PayrollRomms] ROMM ${rommId} has control_ids but none were processed:`, controlIds);
                      }
                    } else {
                      console.log(`[PayrollRomms] ROMM ${rommId} has no control_ids`);
                    }
                  }
                }
              } catch (error) {
                console.error("Error loading controls from Libraries_RCM.json:", error);
              }
            }

            // Load procedures - procedure_id contains procedure names
            // We need to match them with available procedures from ProcedureSelectorModal
            const procedureNames = sharePointResponse.data.romm_library
              .map((entry: any) => {
                const rommId = entry["romm-id"];
                let procedureIds: string[] = [];
                
                // Handle procedure_id field - can be array, string, or undefined
                if (entry.procedure_id !== undefined && entry.procedure_id !== null) {
                  if (Array.isArray(entry.procedure_id)) {
                    procedureIds = entry.procedure_id.map((id: any) => String(id).trim()).filter((id: string) => id.length > 0);
                  } else {
                    const singleId = String(entry.procedure_id).trim();
                    if (singleId.length > 0) {
                      procedureIds = [singleId];
                    }
                  }
                }
                
                console.log(`[PayrollRomms] ROMM ${rommId} has procedure_ids:`, procedureIds);
                
                return {
                  rommId,
                  procedureIds
                };
              })
              .filter((item: any) => item.procedureIds.length > 0);

            // Load available procedures from ProcedureSelectorModal's mock data
            // This is a simplified version - in production, you might want to load from a procedures library
            const availableProcedures: Record<string, SubstantiveProcedure> = {
              "IPE Testing": {
                id: "ipe_testing",
                name: "IPE Testing",
                description: "Information Produced by Entity Testing",
                requiredFiles: ["Pay Register", "CTC Report"],
              },
              "Exception Testing": {
                id: "exception_testing",
                name: "Exception Testing",
                description: "Identify and analyze payroll exceptions",
                requiredFiles: ["Pay Register", "CTC Report"],
              },
              "Increment Analysis": {
                id: "increment_analysis",
                name: "Increment Analysis",
                description: "Analyze salary increments and changes",
                requiredFiles: ["Pay Register", "CTC Report"],
              },
              "Headcount Reconciliation": {
                id: "headcount_reconciliation",
                name: "Headcount Reconciliation",
                description: "Reconcile headcount data",
                requiredFiles: ["Pay Register"],
              },
              "Actuary Testing": {
                id: "actuary_testing",
                name: "Actuary Testing",
                description: "Actuarial valuation testing",
                requiredFiles: ["Actuary Report"],
              },
              "Managerial Remuneration": {
                id: "managerial_remuneration",
                name: "Managerial Remuneration",
                description: "Section 197/198 compliance and Schedule V calculations",
                requiredFiles: ["Directors Report"],
              },
              "Share Based Payment": {
                id: "share_based_payment",
                name: "Share Based Payment",
                description: "Share based payment procedures",
                requiredFiles: [],
              },
            };

            // Match procedure names from ROMM entries with available procedures
            for (const item of procedureNames) {
              const proceduresForRomm: SubstantiveProcedure[] = [];
              
              for (const procedureName of item.procedureIds) {
                // Try exact match first
                let procedure = availableProcedures[procedureName];
                
                // If not found, try case-insensitive match
                if (!procedure) {
                  const matchingKey = Object.keys(availableProcedures).find(
                    key => key.toLowerCase() === procedureName.toLowerCase()
                  );
                  if (matchingKey) {
                    procedure = availableProcedures[matchingKey];
                  }
                }
                
                // If still not found, create a basic procedure entry
                if (!procedure) {
                  procedure = {
                    id: procedureName.toLowerCase().replace(/\s+/g, "_"),
                    name: procedureName,
                    description: `${procedureName} procedure`,
                    requiredFiles: [],
                  };
                }
                
                proceduresForRomm.push(procedure);
              }
              
              if (proceduresForRomm.length > 0) {
                loadedProcedures[item.rommId] = proceduresForRomm;
                console.log(`Loaded ${proceduresForRomm.length} procedures for ROMM ${item.rommId}:`, item.procedureIds);
              }
            }

            // Set the loaded controls and procedures
            setAssociatedControls(loadedControls);
            setAssociatedProcedures(loadedProcedures);
            console.log("Loaded associated controls and procedures from Libraries_Romm.json:", {
              controls: Object.keys(loadedControls).length,
              procedures: Object.keys(loadedProcedures).length
            });

            return; // Successfully loaded from SharePoint
          } else {
            console.warn("SharePoint workspace data not available or empty, trying workspace fallback:", sharePointResponse.error);

            const workspaceFallback = getWorkspaceFallbackRomms(workspace);
            if (workspaceFallback && workspaceFallback.length > 0) {
              setRomms(workspaceFallback);
              setSelectedRommId(workspaceFallback[0].id);
              setRommSelections({});
              setRommDocumentation({});
              return;
            }
          }
        }

        // Fallback to local Instructions.json
        console.log("Falling back to local Instructions.json...");
        const response = await fetch("/payroll/Instructions.json");
        if (!response.ok) {
          throw new Error("Failed to load Instructions.json");
        }
        const instructionsData = await response.json();

        // Extract ROMMS data from the instructions
        const rommsData: RommItem[] = instructionsData.romms || [];

        // If romms array is missing or empty, use sensible defaults
        const effectiveRomms: RommItem[] =
          rommsData.length > 0
            ? rommsData
            : [
                {
                  id: "romm-1",
                  risk: "Payroll expenses is recorded that did not occur",
                  assertion: "Occurrence",
                  options: ["Lower", "Higher", "Significant", "NRPMM"],
                },
                {
                  id: "romm-2",
                  risk: "Payroll expenses is incomplete",
                  assertion: "C",
                  options: ["Lower", "Higher", "Significant", "NRPMM"],
                },
                {
                  id: "romm-3",
                  risk: "Payroll expenses is recorded in incorrect period",
                  assertion: "Cu",
                  options: ["Lower", "Higher", "Significant", "NRPMM"],
                },
                {
                  id: "romm-4",
                  risk: "Payroll expenses is not recorded at proper amount",
                  assertion: "A",
                  options: ["Lower", "Higher", "Significant", "NRPMM"],
                },
                {
                  id: "romm-5",
                  risk: "Payroll expenses is not recorded in proper account",
                  assertion: "Cl",
                  options: ["Lower", "Higher", "Significant", "NRPMM"],
                },
              ];

        setRomms(effectiveRomms);
        if (effectiveRomms.length > 0) {
          setSelectedRommId(effectiveRomms[0].id);
        }
      } catch (error) {
        console.error("Error loading ROMMS data:", error);
        // Final fallback to hardcoded data if all else fails
        const fallbackData: RommItem[] = [
          {
            "id": "EBE.SR.001",
            "risk": "Payroll expenses is recorded that did not occur",
            "assertion": "Occurrence",
            "options": ["Lower", "Higher", "Significant", "NRPMM"]
          },
          {
            "id": "EBE.SR.002",
            "risk": "Payroll expenses is incomplete",
            "assertion": "Completeness",
            "options": ["Lower", "Higher", "Significant", "NRPMM"]
          },
          {
            "id": "EBE.SR.003",
            "risk": "Payroll expenses is recorded in incorrect period",
            "assertion": "Cutoff",
            "options": ["Lower", "Higher", "Significant", "NRPMM"]
          },
          {
            "id": "EBE.SR.004",
            "risk": "Payroll expenses is not recorded at proper amount",
            "assertion": "Accuracy",
            "options": ["Lower", "Higher", "Significant", "NRPMM"]
          },
          {
            "id": "EBE.SR.005",
            "risk": "Payroll expenses is not recorded in proper account",
            "assertion": "Classification",
            "options": ["Lower", "Higher", "Significant", "NRPMM"]
          },
        ];
        setRomms(fallbackData);
        if (fallbackData.length > 0) {
          setSelectedRommId(fallbackData[0].id);
        }
      }
    };

    loadRommsData();
  }, [workspace, rommsOverride]);

  const selectedRomm = romms.find((r) => r.id === selectedRommId);

  const executeRommUpdate = async (payload: {
    rommId: string;
    assessment: string;
    documentation: string;
    controlIds?: string[];
    procedureIds?: string[];
  }) => {
    const primaryResponse = await window.sharePointAPI.updateRommEntry(payload);
    if (primaryResponse?.success) return primaryResponse;

    const errorText = String(primaryResponse?.error || "");
    const isMissingRommError =
      /romm entry/i.test(errorText) && /not found/i.test(errorText);

    if (!isMissingRommError || !window.sharePointAPI?.addRommEntry) {
      return primaryResponse;
    }

    const romm = romms.find((r) => r.id === payload.rommId);
    if (!romm) return primaryResponse;

    const assertionText = Array.isArray(romm.assertion)
      ? romm.assertion.join(", ")
      : String(romm.assertion || "");

    const addResponse = await window.sharePointAPI.addRommEntry({
      "romm-id": payload.rommId,
      workspace: workspace,
      description: romm.risk,
      assertion: assertionText,
    });

    if (!addResponse?.success) {
      return primaryResponse;
    }

    return window.sharePointAPI.updateRommEntry(payload);
  };


  // Extract note lines from Combined.json for the specified workspace
  useEffect(() => {
    try {
      const workspaceEntries = combinedData.data.filter(
        (entry: any) => entry.par === workspace
      );
      
      const uniqueNoteLines = [...new Set(
        workspaceEntries.map((entry: any) => entry.note_line)
      )].filter(Boolean);
      
      setEmployeeBenefitsNoteLines(uniqueNoteLines as string[]);
    } catch (error) {
      console.error(`Error extracting note lines for ${workspace}:`, error);
    }
  }, [workspace]);

  const handleRommSelection = async (rommId: string, option: string) => {
    // Update local state immediately for UI responsiveness
    setRommSelections((prev) => ({
      ...prev,
      [rommId]: option,
    }));

    // Set saving status
    setAutosaveStatus(prev => ({
      ...prev,
      [rommId]: { status: 'saving' }
    }));

    // Autosave to SharePoint
    try {
      if (window.sharePointAPI?.updateRommEntry) {
        console.log(`Autosaving ROMM ${rommId} assessment to SharePoint:`, { 
          rommId, 
          assessment: option, 
          documentation: rommDocumentation[rommId] || "" 
        });
        
        const updateResponse = await executeRommUpdate({
          rommId: rommId,
          assessment: option,
          documentation: rommDocumentation[rommId] || "" // Include existing documentation
        });
        
        console.log(`SharePoint update response for ${rommId}:`, updateResponse);
        
        if (updateResponse.success) {
          console.log(`Successfully autosaved ROMM ${rommId} assessment to SharePoint`);
          setAutosaveStatus(prev => ({
            ...prev,
            [rommId]: { 
              status: 'saved',
              lastSaved: new Date()
            }
          }));
        } else {
          console.error(`Failed to autosave ROMM ${rommId} assessment to SharePoint:`, updateResponse.error);
          setAutosaveStatus(prev => ({
            ...prev,
            [rommId]: { 
              status: 'error',
              error: updateResponse.error || 'Unknown error'
            }
          }));
        }
      } else {
        console.warn("SharePoint API not available for autosave");
        setAutosaveStatus(prev => ({
          ...prev,
          [rommId]: { 
            status: 'error',
            error: 'SharePoint API not available'
          }
        }));
      }
    } catch (error) {
      console.error(`Error autosaving ROMM ${rommId} assessment:`, error);
      setAutosaveStatus(prev => ({
        ...prev,
        [rommId]: { 
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      }));
    }
  };

  // Debounced autosave function for documentation
  const debouncedAutosave = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (rommId: string, documentation: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          // Set saving status
          setAutosaveStatus(prev => ({
            ...prev,
            [rommId]: { status: 'saving' }
          }));

          // Autosave to SharePoint
          try {
            if (window.sharePointAPI?.updateRommEntry) {
              console.log(`Autosaving ROMM ${rommId} documentation to SharePoint:`, { documentation });
              
              const updateResponse = await executeRommUpdate({
                rommId: rommId,
                assessment: rommSelections[rommId] || "", // Include existing assessment
                documentation: documentation
              });
              
              if (updateResponse.success) {
                console.log(`Successfully autosaved ROMM ${rommId} documentation to SharePoint`);
                setAutosaveStatus(prev => ({
                  ...prev,
                  [rommId]: { 
                    status: 'saved',
                    lastSaved: new Date()
                  }
                }));
              } else {
                console.error(`Failed to autosave ROMM ${rommId} documentation to SharePoint:`, updateResponse.error);
                setAutosaveStatus(prev => ({
                  ...prev,
                  [rommId]: { 
                    status: 'error',
                    error: updateResponse.error || 'Unknown error'
                  }
                }));
              }
            } else {
              console.warn("SharePoint API not available for autosave");
              setAutosaveStatus(prev => ({
                ...prev,
                [rommId]: { 
                  status: 'error',
                  error: 'SharePoint API not available'
                }
              }));
            }
          } catch (error) {
            console.error(`Error autosaving ROMM ${rommId} documentation:`, error);
            setAutosaveStatus(prev => ({
              ...prev,
              [rommId]: { 
                status: 'error',
                error: error instanceof Error ? error.message : 'Unknown error'
              }
            }));
          }
        }, 1000); // 1 second delay
      };
    })(),
    [rommSelections]
  );

  const handleDocumentationChange = (rommId: string, documentation: string) => {
    // Update local state immediately for UI responsiveness
    setRommDocumentation((prev) => ({
      ...prev,
      [rommId]: documentation,
    }));

    // Trigger debounced autosave
    debouncedAutosave(rommId, documentation);
  };

  // Helper function to update control workspaces in Libraries_RCM.json and individual control JSON files
  const updateControlWorkspacesInRcm = async (
    controlIds: string[],
    rommId: string,
    addWorkspace: boolean
  ) => {
    try {
      if (!window.sharePointAPI?.downloadRcmJson || !window.sharePointAPI?.uploadRcmJson) {
        console.warn("SharePoint API not available for updating control workspaces");
        return;
      }

      // Get ROMM's workspace
      const romm = romms.find((r) => r.id === rommId);
      if (!romm) {
        console.warn(`ROMM ${rommId} not found, cannot update control workspaces`);
        return;
      }

      // Get ROMM workspace - can be string or array
      const rommWorkspace = romm.workspace || workspace;
      const rommWorkspaceArray = Array.isArray(rommWorkspace)
        ? rommWorkspace
        : rommWorkspace
        ? [rommWorkspace]
        : [];

      if (rommWorkspaceArray.length === 0) {
        console.warn(`ROMM ${rommId} has no workspace, skipping workspace update`);
        return;
      }

      // Download current RCM data
      const rcmRes = await window.sharePointAPI.downloadRcmJson();
      if (!rcmRes.success || !rcmRes.data) {
        console.error("Failed to download Libraries_RCM.json:", rcmRes.error);
        return;
      }

      const rcmData = rcmRes.data;
      const librariesRcm: Record<string, any> = rcmData.libraries_RCM || rcmData.libraries_rcm || {};
      rcmData.libraries_RCM = librariesRcm;

      // Update each control's workspace in both Libraries_RCM.json and individual control JSON files
      for (const controlId of controlIds) {
        const existing = librariesRcm[controlId] || {};
        const currentWorkspace: string[] = Array.isArray(existing.workspace)
          ? [...existing.workspace]
          : existing.workspace
          ? [existing.workspace]
          : [];

        let updatedWorkspace: string[];

        if (addWorkspace) {
          // Add ROMM workspace(s) to control workspace (avoid duplicates)
          updatedWorkspace = [...currentWorkspace];
          rommWorkspaceArray.forEach((ws) => {
            if (!updatedWorkspace.includes(ws)) {
              updatedWorkspace.push(ws);
            }
          });
        } else {
          // Remove ROMM workspace(s) from control workspace
          updatedWorkspace = currentWorkspace.filter(
            (ws) => !rommWorkspaceArray.includes(ws)
          );
        }

        // Update the control entry in Libraries_RCM.json
        librariesRcm[controlId] = {
          ...existing,
          workspace: updatedWorkspace,
          control_name: existing.control_name || existing.controlName || "",
          TypeOfControl: existing.TypeOfControl || existing.typeOfControl || "",
          Nature: existing.Nature || existing.subtype || "",
          romm: existing.romm || [],
        };

        // Update the individual control JSON file's WorkspaceLinked field
        if (window.sharePointAPI?.downloadClientJson && window.sharePointAPI?.uploadFile) {
          try {
            const fileName = `Libraries_InternalControlResponses_${controlId}.json`;
            
            // Download the control JSON file
            const controlJsonRes = await window.sharePointAPI.downloadClientJson(fileName);
            
            if (controlJsonRes.success && controlJsonRes.data) {
              const controlData = controlJsonRes.data;
              
              // Ensure the structure exists
              if (!controlData.InternalControls) {
                controlData.InternalControls = {};
              }
              if (!controlData.InternalControls.Control) {
                controlData.InternalControls.Control = {};
              }
              if (!controlData.InternalControls.Control.ControlSummary) {
                controlData.InternalControls.Control.ControlSummary = {};
              }
              if (!controlData.InternalControls.Control.ControlSummary.ControlDetails) {
                controlData.InternalControls.Control.ControlSummary.ControlDetails = {};
              }
              
              // Update WorkspaceLinked field
              controlData.InternalControls.Control.ControlSummary.ControlDetails.WorkspaceLinked = updatedWorkspace;
              
              // Upload the updated control JSON file
              const fileContent = JSON.stringify(controlData, null, 2);
              const base64Content = typeof btoa === 'function'
                ? btoa(unescape(encodeURIComponent(fileContent)))
                : Buffer.from(fileContent, 'utf8').toString('base64');
              
              const uploadControlRes = await window.sharePointAPI.uploadFile({
                fileName,
                fileContent: base64Content,
                folderName: 'juggernaut',
                referenceValue: `Internal Control: ${existing.control_name || controlId}`,
              });
              
              if (uploadControlRes.success) {
                console.log(`Successfully updated WorkspaceLinked in ${fileName}`);
              } else {
                console.error(`Failed to update WorkspaceLinked in ${fileName}:`, uploadControlRes.error);
              }
            } else {
              console.warn(`Control JSON file ${fileName} not found or could not be downloaded, skipping WorkspaceLinked update`);
            }
          } catch (controlError) {
            console.error(`Error updating WorkspaceLinked for control ${controlId}:`, controlError);
            // Continue with other controls even if one fails
          }
        }
      }

      // Upload updated RCM data
      const uploadRes = await window.sharePointAPI.uploadRcmJson(rcmData);
      if (uploadRes.success) {
        console.log(
          `Successfully updated control workspaces in Libraries_RCM.json for ${addWorkspace ? "adding" : "removing"} workspace(s) ${rommWorkspaceArray.join(", ")}`
        );
      } else {
        console.error("Failed to upload updated Libraries_RCM.json:", uploadRes.error);
      }
    } catch (error) {
      console.error("Error updating control workspaces in Libraries_RCM.json:", error);
    }
  };

  const handleAssociateControls = async (selectedControls: InternalControl[]) => {
    if (selectedRommId) {
      // Update local state
      const newAssociations = {
        ...associatedControls,
        [selectedRommId]: selectedControls
      };
      setAssociatedControls(newAssociations);
      
      // Update payroll metadata
      selectedControls.forEach(control => {
        associateControlWithRomm(control.control_id, selectedRommId, {
          controlId: control.control_id,
          controlName: control.control_name,
          controlType: control.control_type,
          controlAttribute: control.control_attribute,
          controlDescription: control.control_description
        });
      });
      
      // Save ROMM-control linkages to localStorage for persistence (legacy support)
      localStorage.setItem('rommControlLinkages', JSON.stringify(newAssociations));
      
      // Update control workspaces in Libraries_RCM.json - add ROMM workspace to each control
      const controlIds = selectedControls.map(c => c.control_id);
      await updateControlWorkspacesInRcm(controlIds, selectedRommId, true);
      
      // Save to SharePoint
      try {
        if (window.sharePointAPI?.updateRommEntry) {
          console.log(`Saving control associations for ROMM ${selectedRommId} to SharePoint:`, selectedControls.map(c => c.control_id));
          
          const updateResponse = await executeRommUpdate({
            rommId: selectedRommId,
            assessment: rommSelections[selectedRommId] || "",
            documentation: rommDocumentation[selectedRommId] || "",
            controlIds: selectedControls.map(control => control.control_id),
            procedureIds: associatedProcedures[selectedRommId]?.map(p => p.name) || []
          });
          
          if (updateResponse.success) {
            console.log(`Successfully saved control associations for ROMM ${selectedRommId} to SharePoint`);
          } else {
            console.error(`Failed to save control associations for ROMM ${selectedRommId} to SharePoint:`, updateResponse.error);
          }
        } else {
          console.warn("SharePoint API not available for saving control associations");
        }
      } catch (error) {
        console.error(`Error saving control associations for ROMM ${selectedRommId}:`, error);
      }
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('rommControlLinkagesUpdated'));
    }
  };

  const handleRemoveControl = async (rommId: string, controlId: string) => {
    const newAssociations = {
      ...associatedControls,
      [rommId]: associatedControls[rommId]?.filter(control => control.control_id !== controlId) || []
    };
    setAssociatedControls(newAssociations);
    
    // Update payroll metadata
    disassociateControlFromRomm(controlId, rommId);
    
    // Update control workspace in Libraries_RCM.json - remove ROMM workspace from control
    await updateControlWorkspacesInRcm([controlId], rommId, false);
    
    // Save updated ROMM-control linkages to localStorage (legacy support)
    localStorage.setItem('rommControlLinkages', JSON.stringify(newAssociations));
    
    // Save to SharePoint
    try {
      if (window.sharePointAPI?.updateRommEntry) {
        console.log(`Saving updated control associations for ROMM ${rommId} to SharePoint after removal`);
        
        const updateResponse = await executeRommUpdate({
          rommId: rommId,
          assessment: rommSelections[rommId] || "",
          documentation: rommDocumentation[rommId] || "",
          controlIds: newAssociations[rommId]?.map(c => c.control_id) || [],
          procedureIds: associatedProcedures[rommId]?.map(p => p.name) || []
        });
        
        if (updateResponse.success) {
          console.log(`Successfully saved updated control associations for ROMM ${rommId} to SharePoint`);
        } else {
          console.error(`Failed to save updated control associations for ROMM ${rommId} to SharePoint:`, updateResponse.error);
        }
      } else {
        console.warn("SharePoint API not available for saving updated control associations");
      }
    } catch (error) {
      console.error(`Error saving updated control associations for ROMM ${rommId}:`, error);
    }
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('rommControlLinkagesUpdated'));
  };

  const handleAssociateProcedures = async (selectedProcedures: SubstantiveProcedure[]) => {
    if (selectedRommId) {
      setAssociatedProcedures(prev => ({
        ...prev,
        [selectedRommId]: selectedProcedures
      }));

      // Save to SharePoint
      try {
        if (window.sharePointAPI?.updateRommEntry) {
          console.log(`Saving procedure associations for ROMM ${selectedRommId} to SharePoint:`, selectedProcedures.map(p => p.procedure_id));
          
          const updateResponse = await executeRommUpdate({
            rommId: selectedRommId,
            assessment: rommSelections[selectedRommId] || "",
            documentation: rommDocumentation[selectedRommId] || "",
            controlIds: associatedControls[selectedRommId]?.map(c => c.control_id) || [],
            procedureIds: selectedProcedures.map(procedure => procedure.name)
          });
          
          if (updateResponse.success) {
            console.log(`Successfully saved procedure associations for ROMM ${selectedRommId} to SharePoint`);
          } else {
            console.error(`Failed to save procedure associations for ROMM ${selectedRommId} to SharePoint:`, updateResponse.error);
          }
        } else {
          console.warn("SharePoint API not available for saving procedure associations");
        }
      } catch (error) {
        console.error(`Error saving procedure associations for ROMM ${selectedRommId}:`, error);
      }
    }
  };

  const handleRemoveProcedure = async (rommId: string, procedureId: string) => {
    const updatedProcedures = {
      ...associatedProcedures,
      [rommId]: associatedProcedures[rommId]?.filter(procedure => procedure.id !== procedureId) || []
    };
    
    setAssociatedProcedures(updatedProcedures);

    // Save to SharePoint
    try {
      if (window.sharePointAPI?.updateRommEntry) {
        console.log(`Saving updated procedure associations for ROMM ${rommId} to SharePoint after removal`);
        
        const updateResponse = await executeRommUpdate({
          rommId: rommId,
          assessment: rommSelections[rommId] || "",
          documentation: rommDocumentation[rommId] || "",
          controlIds: associatedControls[rommId]?.map(c => c.control_id) || [],
          procedureIds: updatedProcedures[rommId]?.map(p => p.name) || []
        });
        
        if (updateResponse.success) {
          console.log(`Successfully saved updated procedure associations for ROMM ${rommId} to SharePoint`);
        } else {
          console.error(`Failed to save updated procedure associations for ROMM ${rommId} to SharePoint:`, updateResponse.error);
        }
      } else {
        console.warn("SharePoint API not available for saving updated procedure associations");
      }
    } catch (error) {
      console.error(`Error saving updated procedure associations for ROMM ${rommId}:`, error);
    }
  };

  const isRommComplete = (rommId: string) => {
    return rommSelections[rommId] !== undefined;
  };

  const allRommsComplete = romms.every((r) => isRommComplete(r.id));

  const handleControlClickInRomm = async (control: InternalControl) => {
    try {
      const controlId = control.control_id;
      const fileName = `Libraries_InternalControlResponses_${controlId}.json`;

      let controlData: any | null = null;

      // Load latest responses from SharePoint if available
      if (window.sharePointAPI?.downloadClientJson) {
        try {
          const res = await window.sharePointAPI.downloadClientJson(fileName);
          if (res.success && res.data) {
            controlData = res.data;
          }
        } catch (e) {
          console.error("Failed to load control responses from SharePoint", e);
        }
      }

      if (!controlData) {
        controlData = {};
      }

      const controlName =
        controlData.InternalControls?.Control?.ControlSummary?.ControlDetails
          ?.ControlName || control.control_name || "Unknown Control";

      const typeOfControl =
        controlData.InternalControls?.Control?.ControlSummary?.ControlAttributes
          ?.TypeOfControl || control.control_attribute || "Direct";

      const subtype =
        controlData.InternalControls?.Control?.ControlSummary?.ControlAttributes
          ?.Nature || control.control_type || "Manual";

      const workspaceFromFile =
        controlData.InternalControls?.Control?.ControlSummary?.ControlDetails
          ?.WorkspaceLinked || [];

      const controlMetadata = {
        typeOfControl,
        subtype,
        controlId,
        controlName,
        workspace: Array.isArray(workspaceFromFile)
          ? workspaceFromFile
          : workspaceFromFile
          ? [workspaceFromFile]
          : [],
      };

      // Decide template type based on subtype (Manual vs Automated)
      const templateType: "manual" | "automated" =
        subtype === "Automated" ? "automated" : "manual";

      const template =
        templateType === "automated"
          ? CONTROL_TEMPLATES.automated
          : CONTROL_TEMPLATES.manual;

      const combinedControl: any = {
        id: controlId,
        controlMetadata,
        originalFileName: fileName,
        ...controlData,
      };

      setSelectedControlForViewing(combinedControl);
      setControlViewerTemplate(template);
      setIsControlViewerOpen(true);
    } catch (error) {
      console.error("Error loading control for view/edit from SharePoint:", error);
    }
  };

  const handleControlSaveInRomm = (updatedControl: any) => {
    // Update the control in localStorage
    try {
      const savedControls = JSON.parse(localStorage.getItem('internalControls') || '[]');
      const updatedControls = savedControls.map((c: any) => 
        c.id === updatedControl.id ? updatedControl : c
      );
      localStorage.setItem('internalControls', JSON.stringify(updatedControls));
      
      // Trigger refresh of linked controls
      window.dispatchEvent(new CustomEvent('rommControlLinkagesUpdated'));
    } catch (error) {
      console.error('Error saving updated control:', error);
    }
  };

  const getRiskLevelColor = (option: string) => {
    switch (option) {
      case "Lower":
        return "bg-yellow-300/20 text-yellow-300 border-yellow-300/30";
      case "Higher":
        return "bg-yellow-700/30 text-yellow-400 border-yellow-700/30";
      case "Significant":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      case "NRPMM":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const getOptionDescription = (option: string) => {
    switch (option) {
      case "Lower":
        return "Lower risk of material misstatement";
      case "Higher":
        return "Higher risk of material misstatement";
      case "Significant":
        return "Significant risk requiring special audit consideration";
      case "NRPMM":
        return "Not a Risk of Material Misstatement";
      default:
        return "";
    }
  };

  // Handle individual ROMM submission
  // Helper to sync associated controls for a ROMM into Libraries_RCM.json
  // and update WorkspaceLinked in individual control JSON files.
  const syncControlsForRommOnSubmit = async (rommId: string) => {
    const controlsForRomm = associatedControls[rommId] || [];
    if (!controlsForRomm.length || !window.sharePointAPI) {
      return;
    }

    const romm = romms.find((r) => r.id === rommId);
    const rommDescription = romm?.risk || "";
    const userResponse = rommDocumentation[rommId] || "";

    // 1) Update Libraries_RCM.json → add rommId into romm array for each control
    //    and ensure ROMM workspace is added to control workspace
    try {
      if (window.sharePointAPI.downloadRcmJson && window.sharePointAPI.uploadRcmJson) {
        const rcmRes = await window.sharePointAPI.downloadRcmJson();
        if (rcmRes.success) {
          const rcmData = rcmRes.data || {};
          const librariesRcm: Record<string, any> = rcmData.libraries_RCM || rcmData.libraries_rcm || {};
          rcmData.libraries_RCM = librariesRcm;

          // Get ROMM workspace
          const rommWorkspace = romm?.workspace || workspace;
          const rommWorkspaceArray = Array.isArray(rommWorkspace)
            ? rommWorkspace
            : rommWorkspace
            ? [rommWorkspace]
            : [];

          // Update each control's workspace in both Libraries_RCM.json and individual control JSON files
          for (const control of controlsForRomm) {
            const key = control.control_id;
            const existing = librariesRcm[key] || {};
            const rommArray: string[] = Array.isArray(existing.romm)
              ? [...existing.romm.map(String)]
              : [];
            if (!rommArray.includes(rommId)) {
              rommArray.push(rommId);
            }

            // Update workspace - add ROMM workspace if not already present
            const currentWorkspace: string[] = Array.isArray(existing.workspace)
              ? [...existing.workspace]
              : existing.workspace
              ? [existing.workspace]
              : [];
            
            const updatedWorkspace = [...currentWorkspace];
            rommWorkspaceArray.forEach((ws) => {
              if (!updatedWorkspace.includes(ws)) {
                updatedWorkspace.push(ws);
              }
            });

            librariesRcm[key] = {
              ...existing,
              romm: rommArray,
              control_name: existing.control_name || existing.controlName || control.control_name,
              TypeOfControl: existing.TypeOfControl || existing.typeOfControl || control.control_attribute,
              Nature: existing.Nature || existing.subtype || control.control_type,
              workspace: updatedWorkspace,
            };

            // Update the individual control JSON file's WorkspaceLinked field
            if (window.sharePointAPI?.downloadClientJson && window.sharePointAPI?.uploadFile) {
              try {
                const fileName = `Libraries_InternalControlResponses_${key}.json`;
                
                // Download the control JSON file
                const controlJsonRes = await window.sharePointAPI.downloadClientJson(fileName);
                
                if (controlJsonRes.success && controlJsonRes.data) {
                  const controlData = controlJsonRes.data;
                  
                  // Ensure the structure exists
                  if (!controlData.InternalControls) {
                    controlData.InternalControls = {};
                  }
                  if (!controlData.InternalControls.Control) {
                    controlData.InternalControls.Control = {};
                  }
                  if (!controlData.InternalControls.Control.ControlSummary) {
                    controlData.InternalControls.Control.ControlSummary = {};
                  }
                  if (!controlData.InternalControls.Control.ControlSummary.ControlDetails) {
                    controlData.InternalControls.Control.ControlSummary.ControlDetails = {};
                  }
                  
                  // Update WorkspaceLinked field
                  controlData.InternalControls.Control.ControlSummary.ControlDetails.WorkspaceLinked = updatedWorkspace;
                  
                  // Upload the updated control JSON file
                  const fileContent = JSON.stringify(controlData, null, 2);
                  const base64Content = typeof btoa === 'function'
                    ? btoa(unescape(encodeURIComponent(fileContent)))
                    : Buffer.from(fileContent, 'utf8').toString('base64');
                  
                  const uploadControlRes = await window.sharePointAPI.uploadFile({
                    fileName,
                    fileContent: base64Content,
                    folderName: 'juggernaut',
                    referenceValue: `Internal Control: ${existing.control_name || control.control_name || key}`,
                  });
                  
                  if (uploadControlRes.success) {
                    console.log(`Successfully updated WorkspaceLinked in ${fileName} during ROMM submit`);
                  } else {
                    console.error(`Failed to update WorkspaceLinked in ${fileName}:`, uploadControlRes.error);
                  }
                } else {
                  console.warn(`Control JSON file ${fileName} not found or could not be downloaded during ROMM submit, skipping WorkspaceLinked update`);
                }
              } catch (controlError) {
                console.error(`Error updating WorkspaceLinked for control ${key} during ROMM submit:`, controlError);
                // Continue with other controls even if one fails
              }
            }
          }

          await window.sharePointAPI.uploadRcmJson(rcmData);
        }
      }
    } catch (e) {
      console.error("Error updating Libraries_RCM.json for ROMM submit:", e);
    }

    // 2) Previously, this also updated each control's
    //    Libraries_InternalControlResponses_<control_id>.json file
    //    with ROMM-related design factors. That behavior has been
    //    explicitly disabled per latest requirements, so no further
    //    writes are performed here.
  };

  const handleSubmitRomm = async (rommId: string) => {
    try {
      // Indicate explicit submit/save in progress
      setAutosaveStatus(prev => ({
        ...prev,
        [rommId]: { status: 'saving' }
      }));

      console.log(`Submitting ROMM ${rommId} to SharePoint:`, {
        rommId,
        assessment: rommSelections[rommId] || "",
        documentation: rommDocumentation[rommId] || "",
        controlIds: associatedControls[rommId]?.map(c => c.control_id) || [],
        procedureNames: associatedProcedures[rommId]?.map(p => p.name) || []
      });

      if (window.sharePointAPI?.updateRommEntry) {
        const updateResponse = await executeRommUpdate({
          rommId: rommId,
          assessment: rommSelections[rommId] || "",
          documentation: rommDocumentation[rommId] || "",
          controlIds: associatedControls[rommId]?.map(c => c.control_id) || [],
          procedureIds: associatedProcedures[rommId]?.map(p => p.name) || [] // Map to procedure names as requested
        });

        if (updateResponse.success) {
          // Also sync ROMM ↔ control linkage into Libraries_RCM.json.
          // NOTE: This no longer writes to any
          // Libraries_InternalControlResponses_<control_id>.json files.
          await syncControlsForRommOnSubmit(rommId);
          console.log(`Successfully submitted ROMM ${rommId} to SharePoint`);
          // Show success feedback
          setAutosaveStatus(prev => ({
            ...prev,
            [rommId]: { 
              status: 'saved',
              lastSaved: new Date()
            }
          }));
        } else {
          console.error(`Failed to submit ROMM ${rommId} to SharePoint:`, updateResponse.error);
          setAutosaveStatus(prev => ({
            ...prev,
            [rommId]: { 
              status: 'error',
              error: updateResponse.error || 'Unknown error'
            }
          }));
        }
      } else {
        console.warn("SharePoint API not available for submission");
        setAutosaveStatus(prev => ({
          ...prev,
          [rommId]: { 
            status: 'error',
            error: 'SharePoint API not available'
          }
        }));
      }
    } catch (error) {
      console.error(`Error submitting ROMM ${rommId}:`, error);
      setAutosaveStatus(prev => ({
        ...prev,
        [rommId]: { 
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      }));
    }
  };

  const handleComplete = () => {
    // Data is already saved via autosave, just complete the form
    const completeData = {
      selections: rommSelections,
      documentation: rommDocumentation,
    };
    onComplete(completeData);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">
            Risk of Material Misstatement (RoMMs) - {workspace}
          </h2>
          <p className="text-sm text-white/60">
            Assess the risk level for each assertion
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-white/10 text-white/80">
            {Object.keys(rommSelections).length} / {romms.length} Complete
          </Badge>
          
          {/* Autosave Status Indicator */}
          {selectedRommId && autosaveStatus[selectedRommId] && (
            <div className="flex items-center gap-2">
              {autosaveStatus[selectedRommId].status === 'saving' && (
                <div className="flex items-center gap-1 text-yellow-400">
                  <div className="h-2 w-2 bg-yellow-400 rounded-full animate-pulse"></div>
                  <span className="text-xs">Saving...</span>
                </div>
              )}
              {autosaveStatus[selectedRommId].status === 'saved' && (
                <div className="flex items-center gap-1 text-green-400">
                  <div className="h-2 w-2 bg-green-400 rounded-full"></div>
                  <span className="text-xs">
                    Saved {autosaveStatus[selectedRommId].lastSaved && 
                      `at ${autosaveStatus[selectedRommId].lastSaved!.toLocaleTimeString()}`}
                  </span>
                </div>
              )}
              {autosaveStatus[selectedRommId].status === 'error' && (
                <div className="flex items-center gap-1 text-red-400">
                  <div className="h-2 w-2 bg-red-400 rounded-full"></div>
                  <span className="text-xs">Save failed</span>
                </div>
              )}
            </div>
          )}
          
          {/* Debug: Manual test button */}
          {selectedRommId && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={async () => {
                console.log("Manual test of autosave for:", selectedRommId);
                if (window.sharePointAPI?.updateRommEntry) {
                  try {
                    const response = await window.sharePointAPI.updateRommEntry({
                      rommId: selectedRommId,
                      assessment: "Test",
                      documentation: "Manual test"
                    });
                    console.log("Manual test response:", response);
                  } catch (error) {
                    console.error("Manual test error:", error);
                  }
                }
              }}
              className="text-xs"
            >
              Test Save
            </Button>
          )}
        </div>
      </div>

      {/* Two Column Layout - 30% Left, 70% Right */}
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Left Panel - ROMMS List */}
        <Card className="border-white/10 bg-white/5 text-white lg:w-[30%] lg:flex-shrink-0">
          <CardHeader>
            <CardTitle className="text-lg">{workspace} Risks</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Search Bar */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
              <Input
                type="text"
                placeholder="Search ROMMs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white/5 border-white/20 pl-9 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20"
                disabled={isArchivalMode}
              />
            </div>
            {/* Filtered ROMMs List */}
            {romms
              .filter((romm) => {
                const query = searchQuery.toLowerCase();
                return (
                  (romm.id && String(romm.id).toLowerCase().includes(query)) ||
                  (romm.risk && String(romm.risk).toLowerCase().includes(query)) ||
                  (romm.assertion && String(romm.assertion).toLowerCase().includes(query))
                );
              })
              .map((romm) => (
              <div
                key={romm.id}
                className={`cursor-pointer rounded-lg border p-3 transition-all ${
                  selectedRommId === romm.id
                    ? "border-blue-500/50 bg-blue-500/10"
                    : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"
                }`}
                onClick={() => setSelectedRommId(romm.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="mb-2 flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {romm.id}
                      </Badge>
                      {isRommComplete(romm.id) ? (
                        <CheckCircle2 className="h-4 w-4 text-green-400" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-yellow-400" />
                      )}
                    </div>
                    <p className="text-sm leading-relaxed text-white/80">
                      {romm.risk}
                    </p>
                    {rommSelections[romm.id] && (
                      <div className="mt-2">
                        {rommSelections[romm.id] === 'Lower' && 
                          <svg width="16" height="16" viewBox="0 0 16 16" style={{verticalAlign: 'middle'}}>
                            <defs>
                              <mask id={`lowerMaskLeft${romm.id}`}>
                                <rect width="16" height="16" fill="black"/>
                                <polygon points="8,2 14,14 2,14" fill="white"/>
                              </mask>
                            </defs>
                            <polygon points="8,2 14,14 2,14" fill="none" stroke="#CCCC00" strokeWidth="1"/>
                            <rect x="0" y="10" width="16" height="6" fill="#CCCC00" mask={`url(#lowerMaskLeft${romm.id})`}/>
                          </svg>
                        }
                        {rommSelections[romm.id] === 'Higher' && 
                          <svg width="16" height="16" viewBox="0 0 16 16" style={{verticalAlign: 'middle'}}>
                            <defs>
                              <mask id={`higherMaskLeft${romm.id}`}>
                                <rect width="16" height="16" fill="black"/>
                                <polygon points="8,2 14,14 2,14" fill="white"/>
                              </mask>
                            </defs>
                            <polygon points="8,2 14,14 2,14" fill="none" stroke="#FFA500" strokeWidth="1"/>
                            <rect x="0" y="8" width="16" height="8" fill="#FFA500" mask={`url(#higherMaskLeft${romm.id})`}/>
                          </svg>
                        }
                        {rommSelections[romm.id] === 'Significant' && 
                          <svg width="16" height="16" viewBox="0 0 16 16" style={{verticalAlign: 'middle'}}>
                            <polygon points="8,2 14,14 2,14" fill="#FF0000"/>
                          </svg>
                        }
                        {rommSelections[romm.id] === 'NRPMM' && 
                          <svg width="16" height="16" viewBox="0 0 16 16" style={{verticalAlign: 'middle'}}>
                            <polygon points="8,2 14,14 2,14" fill="none" stroke="#008000" strokeWidth="2"/>
                          </svg>
                        }
                      </div>
                    )}
                  </div>
                </div>
              </div>
              ))}
            {romms.filter((romm) => {
              const query = searchQuery.toLowerCase();
              return (
                (romm.id && String(romm.id).toLowerCase().includes(query)) ||
                (romm.risk && String(romm.risk).toLowerCase().includes(query)) ||
                (romm.assertion && String(romm.assertion).toLowerCase().includes(query))
              );
            }).length === 0 && searchQuery && (
              <div className="text-center py-6 text-white/60 text-sm">
                No ROMMs found matching "{searchQuery}"
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Panel - Four Column Layout */}
        <Card className="border-white/10 bg-white/5 text-white lg:w-[70%] lg:flex-shrink-0">
          <CardHeader>
            <CardTitle className="text-lg">
              {selectedRomm?.id || "Select a ROMM"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Table Header */}
            <div className="mb-4 hidden rounded-lg border border-white/20 bg-white/10 p-3 md:grid md:grid-cols-4 md:gap-4">
              <div className="text-sm font-semibold text-white">Description</div>
              <div className="text-sm font-semibold text-white">Accounts/Areas</div>
              <div className="text-sm font-semibold text-white">Assertions/Type</div>
              <div className="text-sm font-semibold text-white">Assessment</div>
            </div>

            {/* Table Rows - Show only selected ROMM */}
            <div className="space-y-4">
              {selectedRomm && (
                <div
                  key={selectedRomm.id}
                  className="rounded-lg border border-white/10 bg-white/5 p-4 transition-colors hover:bg-white/10 md:grid md:grid-cols-4 md:gap-4 md:p-3"
                >
                  {/* Description Column */}
                  <div className="mb-4 md:mb-0">
                    <div className="mb-2 block text-xs font-semibold text-white/60 md:hidden">Description</div>
                    <div className="text-sm text-white/80">
                      {selectedRomm.risk}
                    </div>
                  </div>

                  {/* Accounts/Areas Column */}
                  <div className="mb-4 md:mb-0">
                    <div className="mb-2 block text-xs font-semibold text-white/60 md:hidden">Accounts/Areas</div>
                    <div className="space-y-1">
                      {employeeBenefitsNoteLines.length > 0 ? (
                        employeeBenefitsNoteLines.map((noteLine, index) => (
                          <div key={index} className="flex items-center gap-1 text-xs text-orange-400">
                            <span className="h-2 w-2 rounded-full bg-orange-400"></span>
                            {noteLine}
                          </div>
                        ))
                      ) : (
                        <div className="text-xs text-white/60">Loading accounts/areas...</div>
                      )}
                    </div>
                  </div>

                  {/* Assertions/Type Column */}
                  <div className="mb-4 md:mb-0">
                    <div className="mb-2 block text-xs font-semibold text-white/60 md:hidden">Assertions/Type</div>
                    <div className="space-y-2">
                      {parseAssertions(selectedRomm.assertion).map((assertion, index) => (
                        <div key={index} className="flex items-center">
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 text-xs font-bold text-white">
                            {getAssertionAbbreviation(assertion)}
                          </div>
                          <span className="ml-2 text-sm text-white">{assertion}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Assessment Column */}
                  <div>
                    <div className="mb-2 block text-xs font-semibold text-white/60 md:hidden">Assessment</div>
                    <div className="flex items-center gap-2">
                      {/* Triangle indicator on the left */}
                      <div className="flex-shrink-0">
                        {rommSelections[selectedRomm.id] === 'Lower' && 
                          <svg width="16" height="16" viewBox="0 0 16 16" style={{verticalAlign: 'middle'}}>
                            <defs>
                              <mask id="lowerMask">
                                <rect width="16" height="16" fill="black"/>
                                <polygon points="8,2 14,14 2,14" fill="white"/>
                              </mask>
                            </defs>
                            <polygon points="8,2 14,14 2,14" fill="none" stroke="#CCCC00" strokeWidth="1"/>
                            <rect x="0" y="10" width="16" height="6" fill="#CCCC00" mask="url(#lowerMask)"/>
                          </svg>
                        }
                        {rommSelections[selectedRomm.id] === 'Higher' && 
                          <svg width="16" height="16" viewBox="0 0 16 16" style={{verticalAlign: 'middle'}}>
                            <defs>
                              <mask id="higherMask">
                                <rect width="16" height="16" fill="black"/>
                                <polygon points="8,2 14,14 2,14" fill="white"/>
                              </mask>
                            </defs>
                            <polygon points="8,2 14,14 2,14" fill="none" stroke="#FFA500" strokeWidth="1"/>
                            <rect x="0" y="8" width="16" height="8" fill="#FFA500" mask="url(#higherMask)"/>
                          </svg>
                        }
                        {rommSelections[selectedRomm.id] === 'Significant' && 
                          <svg width="16" height="16" viewBox="0 0 16 16" style={{verticalAlign: 'middle'}}>
                            <polygon points="8,2 14,14 2,14" fill="#FF0000"/>
                          </svg>
                        }
                        {rommSelections[selectedRomm.id] === 'NRPMM' && 
                          <svg width="16" height="16" viewBox="0 0 16 16" style={{verticalAlign: 'middle'}}>
                            <polygon points="8,2 14,14 2,14" fill="none" stroke="#008000" strokeWidth="2"/>
                          </svg>
                        }
                      </div>
                      <Select
                        value={rommSelections[selectedRomm.id] || ""}
                        onValueChange={(value) => handleRommSelection(selectedRomm.id, value)}
                        disabled={isArchivalMode}
                      >
                        <SelectTrigger className="w-full">
                           <SelectValue placeholder="Select assessment">
                             {rommSelections[selectedRomm.id] && (
                               <div className="flex items-center gap-2">
                                 {rommSelections[selectedRomm.id] === 'Lower' && 
                                   <svg width="16" height="16" viewBox="0 0 16 16" style={{verticalAlign: 'middle'}}>
                                     <defs>
                                       <mask id="lowerMaskSelect">
                                         <rect width="16" height="16" fill="black"/>
                                         <polygon points="8,2 14,14 2,14" fill="white"/>
                                       </mask>
                                     </defs>
                                     <polygon points="8,2 14,14 2,14" fill="none" stroke="#CCCC00" strokeWidth="1"/>
                                     <rect x="0" y="10" width="16" height="6" fill="#CCCC00" mask="url(#lowerMaskSelect)"/>
                                   </svg>
                                 }
                                 {rommSelections[selectedRomm.id] === 'Higher' && 
                                   <svg width="16" height="16" viewBox="0 0 16 16" style={{verticalAlign: 'middle'}}>
                                     <defs>
                                       <mask id="higherMaskSelect">
                                         <rect width="16" height="16" fill="black"/>
                                         <polygon points="8,2 14,14 2,14" fill="white"/>
                                       </mask>
                                     </defs>
                                     <polygon points="8,2 14,14 2,14" fill="none" stroke="#FFA500" strokeWidth="1"/>
                                     <rect x="0" y="8" width="16" height="8" fill="#FFA500" mask="url(#higherMaskSelect)"/>
                                   </svg>
                                 }
                                 {rommSelections[selectedRomm.id] === 'Significant' && 
                                   <svg width="16" height="16" viewBox="0 0 16 16" style={{verticalAlign: 'middle'}}>
                                     <polygon points="8,2 14,14 2,14" fill="#FF0000"/>
                                   </svg>
                                 }
                                 {rommSelections[selectedRomm.id] === 'NRPMM' && 
                                   <svg width="16" height="16" viewBox="0 0 16 16" style={{verticalAlign: 'middle'}}>
                                     <polygon points="8,2 14,14 2,14" fill="none" stroke="#008000" strokeWidth="2"/>
                                   </svg>
                                 }
                                 <Badge className={`text-xs ${getRiskLevelColor(rommSelections[selectedRomm.id])}`}>
                                   {rommSelections[selectedRomm.id]}
                                 </Badge>
                               </div>
                             )}
                           </SelectValue>
                         </SelectTrigger>
                        <SelectContent>
                           {selectedRomm.options.map((option) => (
                             <SelectItem key={option} value={option}>
                               <div className="flex items-center gap-2">
                                 {option === 'Lower' && 
                                   <svg width="16" height="16" viewBox="0 0 16 16" style={{verticalAlign: 'middle'}}>
                                     <defs>
                                       <mask id={`lowerMaskItem${option}`}>
                                         <rect width="16" height="16" fill="black"/>
                                         <polygon points="8,2 14,14 2,14" fill="white"/>
                                       </mask>
                                     </defs>
                                     <polygon points="8,2 14,14 2,14" fill="none" stroke="#CCCC00" strokeWidth="1"/>
                                     <rect x="0" y="10" width="16" height="6" fill="#CCCC00" mask={`url(#lowerMaskItem${option})`}/>
                                   </svg>
                                 }
                                 {option === 'Higher' && 
                                   <svg width="16" height="16" viewBox="0 0 16 16" style={{verticalAlign: 'middle'}}>
                                     <defs>
                                       <mask id={`higherMaskItem${option}`}>
                                         <rect width="16" height="16" fill="black"/>
                                         <polygon points="8,2 14,14 2,14" fill="white"/>
                                       </mask>
                                     </defs>
                                     <polygon points="8,2 14,14 2,14" fill="none" stroke="#FFA500" strokeWidth="1"/>
                                     <rect x="0" y="8" width="16" height="8" fill="#FFA500" mask={`url(#higherMaskItem${option})`}/>
                                   </svg>
                                 }
                                 {option === 'Significant' && 
                                   <svg width="16" height="16" viewBox="0 0 16 16" style={{verticalAlign: 'middle'}}>
                                     <polygon points="8,2 14,14 2,14" fill="#FF0000"/>
                                   </svg>
                                 }
                                 {option === 'NRPMM' && 
                                   <svg width="16" height="16" viewBox="0 0 16 16" style={{verticalAlign: 'middle'}}>
                                     <polygon points="8,2 14,14 2,14" fill="none" stroke="#008000" strokeWidth="2"/>
                                   </svg>
                                 }
                                 <Badge className={`text-xs ${getRiskLevelColor(option)}`}>
                                   {option}
                                 </Badge>
                               </div>
                             </SelectItem>
                           ))}
                         </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Risk Assessment Documentation */}
            {selectedRomm && (
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h4 className="text-md font-medium text-white">
                      Risk Assessment Documentation
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setReviewCommentOpen(true)}
                      className="flex-shrink-0 p-1 text-yellow-400 hover:text-yellow-300 hover:bg-white/10 rounded transition-colors"
                      aria-label="Review Comments"
                    >
                      <MessageSquare className="h-4 w-4" />
                    </Button>
                  </div>
                  {(() => {
                    const currentStatus = autosaveStatus[selectedRomm.id];
                    const isSubmitting = currentStatus?.status === 'saving';
                    return (
                  <Button
                      onClick={() => handleSubmitRomm(selectedRomm.id)}
                      className="bg-green-600 text-white hover:bg-green-700"
                      disabled={!rommSelections[selectedRomm.id] || isSubmitting || isArchivalMode}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Saving...
                        </span>
                      ) : (
                        "Submit to SharePoint"
                      )}
                    </Button>
                    );
                  })()}
                </div>
                <Textarea
                  placeholder="Enter your risk assessment documentation here..."
                  value={rommDocumentation[selectedRomm.id] || ""}
                  onChange={(e) => handleDocumentationChange(selectedRomm.id, e.target.value)}
                  className="min-h-[120px] bg-white/5 border-white/20 text-white placeholder:text-white/50 focus:border-blue-500/50 focus:ring-blue-500/20"
                  disabled={isArchivalMode}
                />
              </div>
            )}

            {/* Associated Controls */}
            {selectedRomm && (
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-md font-medium text-white flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Associated Controls
                  </h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsControlSelectorOpen(true)}
                    className="flex items-center gap-2 border-white/20 bg-white/5 text-white hover:bg-white/10"
                    disabled={isArchivalMode}
                  >
                    <Plus className="h-4 w-4" />
                    Associate Control
                  </Button>
                </div>
                
                {associatedControls[selectedRomm.id] && associatedControls[selectedRomm.id].length > 0 ? (
                  <div className="space-y-2">
                    {associatedControls[selectedRomm.id].map((control) => (
                      <Card key={control.control_id} className="border-white/10 bg-white/5 cursor-pointer transition-all hover:border-white/20 hover:bg-white/10">
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between">
                            <div 
                              className="flex-1"
                              onClick={() => handleControlClickInRomm(control)}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="text-sm font-medium text-white">
                                  {control.control_name}
                                </h5>
                                <Badge variant="outline" className="text-xs">
                                  {control.control_id}
                                </Badge>
                              </div>
                              <p className="text-xs text-white/70 mb-2">
                                {control.control_description}
                              </p>
                              <div className="flex gap-2">
                                <Badge variant="outline" className="text-xs">
                                  {control.control_type}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {control.control_attribute}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="text-white/40">
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveControl(selectedRomm.id, control.control_id);
                                }}
                                className="text-white/60 hover:text-white hover:bg-white/10"
                                disabled={isArchivalMode}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-white/60 border border-white/10 rounded-lg bg-white/5">
                    <Shield className="h-8 w-8 mx-auto mb-2 text-white/40" />
                    <p className="text-sm">No controls associated with this ROMM</p>
                    <p className="text-xs mt-1">Click "Associate Control" to add controls from the Internal Control Library</p>
                  </div>
                )}
              </div>
            )}

            {/* Substantive Procedures */}
            {selectedRomm && (
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-md font-medium text-white flex items-center gap-2">
                    <Play className="h-4 w-4" />
                    Substantive Procedures
                  </h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsProcedureSelectorOpen(true)}
                    className="flex items-center gap-2 border-white/20 bg-white/5 text-white hover:bg-white/10"
                    disabled={isArchivalMode}
                  >
                    <Plus className="h-4 w-4" />
                    Associate Procedure
                  </Button>
                </div>
                
                {associatedProcedures[selectedRomm.id] && associatedProcedures[selectedRomm.id].length > 0 ? (
                  <div className="space-y-2">
                    {associatedProcedures[selectedRomm.id].map((procedure) => (
                      <Card key={procedure.id} className="border-white/10 bg-white/5">
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="text-sm font-medium text-white">
                                  {procedure.name}
                                </h5>
                                <Badge variant="outline" className="text-xs">
                                  {procedure.id}
                                </Badge>
                              </div>
                              <p className="text-xs text-white/70 mb-2">
                                {procedure.description}
                              </p>
                              
                              {/* Required Files */}
                              <div className="space-y-1 mb-2">
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-white/60">Required Files:</span>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {procedure.requiredFiles.map((file, index) => (
                                    <Badge key={index} variant="outline" className="text-xs">
                                      {file}
                                    </Badge>
                                  ))}
                                </div>
                              </div>

                              {/* Questions Count */}
                              {procedure.questions && procedure.questions.length > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  {procedure.questions.length} question(s)
                                </Badge>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveProcedure(selectedRomm.id, procedure.id)}
                              className="text-white/60 hover:text-white hover:bg-white/10"
                              disabled={isArchivalMode}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-white/60 border border-white/10 rounded-lg bg-white/5">
                    <Play className="h-8 w-8 mx-auto mb-2 text-white/40" />
                    <p className="text-sm">No procedures associated with this ROMM</p>
                    <p className="text-xs mt-1">Click "Associate Procedure" to add procedures from Substantive Procedures</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 p-4">
        <div className="text-sm text-white/60">
          Complete all risk assessments to proceed to the next step
        </div>
        <Button
          onClick={handleComplete}
          disabled={!allRommsComplete || isArchivalMode}
          className="bg-blue-600 text-white hover:bg-blue-700"
        >
          Complete Risk Assessment
        </Button>
      </div>

      {/* Control Selector Modal */}
      <ControlSelectorModal
        isOpen={isControlSelectorOpen}
        onClose={() => setIsControlSelectorOpen(false)}
        onControlsSelected={handleAssociateControls}
        existingControls={selectedRommId ? associatedControls[selectedRommId] || [] : []}
      />

      {/* Procedure Selector Modal */}
      <ProcedureSelectorModal
        isOpen={isProcedureSelectorOpen}
        onClose={() => setIsProcedureSelectorOpen(false)}
        onProceduresSelected={handleAssociateProcedures}
        existingProcedures={selectedRommId ? associatedProcedures[selectedRommId] || [] : []}
      />
      
      {/* Control Viewer Modal */}
      <InternalControlViewerModal
        isOpen={isControlViewerOpen}
        onClose={() => setIsControlViewerOpen(false)}
        onSave={handleControlSaveInRomm}
        control={selectedControlForViewing}
        template={controlViewerTemplate}
        mode="view"
      />

      {/* Review Comment Modal */}
      {selectedRommId && (
        <ReviewComment
          questionId={selectedRommId}
          questionText={`Risk Assessment Documentation - ${selectedRommId}`}
          isOpen={reviewCommentOpen}
          onClose={() => setReviewCommentOpen(false)}
        />
      )}
    </div>
  );
}
